import React from 'react'
import {Box, Typography} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import Card from 'src/components-new/layout/Card'
import Button from 'src/components-new/primitives/Button'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import TimePickerNew from 'src/components-new/inputs/TimePickerNew'
import CheckboxGroupNew from 'src/components-new/inputs/CheckboxGroupNew'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import UnitValueField from './UnitValueField'
import {CustomDosage, DosageValue} from './secondaryTherapyFormUtils'
import {
  CALCULATION_CRITERION_UNIT_OPTIONS,
  DEFAULT_DOSAGE_UNIT_TYPE,
  DOSAGE_UNIT_OPTIONS,
  INFUSION_RATE_UNIT_OPTIONS,
  VIAL_DOSAGE_UNIT_OPTIONS,
} from '../../patient/tabs/therapy/therapyOptions'
import {SecondaryTherapyDataSchemaElement} from 'src/constants/secondaryTherapyFields'

// New-style equivalent of src/components/templates/TherapyDialogs/DosageFields.tsx.
// The legacy "Personalizza dosaggi" Form.List is replaced by a plain array in state.

interface DosageStepProps {
  value: DosageValue
  onChange: (value: DosageValue) => void
  dataSchema: SecondaryTherapyDataSchemaElement[]
}

const EMPTY_CUSTOM_DOSAGE: CustomDosage = {
  dosage: {unitType: DEFAULT_DOSAGE_UNIT_TYPE},
  infusionRate: {},
  secondaryDosage: {},
  dilution: undefined,
  administrationHour: null,
}

export default function DosageStep({value, onChange, dataSchema}: DosageStepProps): JSX.Element {
  const set = (patch: Partial<DosageValue>): void => onChange({...value, ...patch})
  const isCustomDosagesEnabled = Boolean(value.customDosages?.length)
  const isFieldVisible = (key: string): boolean => dataSchema.some((element) => element.value === key)
  const fieldLabel = (key: string, fallback: string): string => dataSchema.find((element) => element.value === key)?.name ?? fallback

  const onCustomDosagesToggle = (checked: string[]): void => {
    const enabled = checked.includes('custom')
    set({customDosages: enabled ? [{...EMPTY_CUSTOM_DOSAGE}] : undefined})
  }

  const updateCustomDosage = (index: number, patch: Partial<CustomDosage>): void => {
    const next = (value.customDosages ?? []).map((customDosage, i) => (i === index ? {...customDosage, ...patch} : customDosage))
    set({customDosages: next})
  }

  const addCustomDosage = (): void => {
    set({customDosages: [...(value.customDosages ?? []), {...EMPTY_CUSTOM_DOSAGE}]})
  }

  const removeCustomDosage = (index: number): void => {
    set({customDosages: (value.customDosages ?? []).filter((_, i) => i !== index)})
  }

  return (
    <Box>
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: 2, mb: 2}}>
        {isFieldVisible('assumptionDosage') && (
          <UnitValueField
            label={fieldLabel('assumptionDosage', 'Dosaggio per assunzione')}
            value={value.assumptionDosage}
            onChange={(assumptionDosage) => set({assumptionDosage})}
            unitOptions={DOSAGE_UNIT_OPTIONS}
          />
        )}
        {isFieldVisible('assumptionDosage.infusionRate') && (
          <UnitValueField
            label={fieldLabel('assumptionDosage.infusionRate', 'Velocità infusionale')}
            value={value.infusionRate}
            onChange={(infusionRate) => set({infusionRate})}
            unitOptions={INFUSION_RATE_UNIT_OPTIONS}
          />
        )}
        {isFieldVisible('assumptionDosage.secondaryDosage') && (
          <UnitValueField
            label={fieldLabel('assumptionDosage.secondaryDosage', 'Dosaggio secondario')}
            value={value.secondaryDosage}
            onChange={(secondaryDosage) => set({secondaryDosage})}
            unitOptions={DOSAGE_UNIT_OPTIONS}
          />
        )}
        {isFieldVisible('assumptionDosage.dilution') && (
          <TextFieldNew
            label={fieldLabel('assumptionDosage.dilution', 'Diluizione (ml)')}
            fullWidth
            value={value.dilution !== undefined && value.dilution !== null ? String(value.dilution) : ''}
            onChange={(v) => set({dilution: v === '' ? undefined : Number(v)})}
          />
        )}
        {isFieldVisible('drugDosage') && (
          <UnitValueField
            label={fieldLabel('drugDosage', 'Dosaggio singolo')}
            value={value.drugDosage}
            onChange={(drugDosage) => set({drugDosage})}
            unitOptions={DOSAGE_UNIT_OPTIONS}
          />
        )}
        {isFieldVisible('dailyDosage') && (
          <UnitValueField
            label={fieldLabel('dailyDosage', 'Dosaggio giornaliero')}
            value={value.dailyDosage}
            onChange={(dailyDosage) => set({dailyDosage})}
            unitOptions={DOSAGE_UNIT_OPTIONS}
          />
        )}
        {isFieldVisible('weeklyDosage') && (
          <UnitValueField
            label={fieldLabel('weeklyDosage', 'Dosaggio settimanale')}
            value={value.weeklyDosage}
            onChange={(weeklyDosage) => set({weeklyDosage})}
            unitOptions={DOSAGE_UNIT_OPTIONS}
          />
        )}
        {isFieldVisible('monthlyDosage') && (
          <UnitValueField
            label={fieldLabel('monthlyDosage', 'Dosaggio mensile')}
            value={value.monthlyDosage}
            onChange={(monthlyDosage) => set({monthlyDosage})}
            unitOptions={DOSAGE_UNIT_OPTIONS}
          />
        )}
        {isFieldVisible('vialDosage') && (
          <UnitValueField
            label={fieldLabel('vialDosage', 'Dosaggio fiala')}
            value={value.vialDosage}
            onChange={(vialDosage) => set({vialDosage})}
            unitOptions={VIAL_DOSAGE_UNIT_OPTIONS}
          />
        )}
        {isFieldVisible('calculationCriterion') && (
          <UnitValueField
            label={fieldLabel('calculationCriterion', 'Criterio di calcolo')}
            value={value.calculationCriterion}
            onChange={(calculationCriterion) => set({calculationCriterion})}
            unitOptions={CALCULATION_CRITERION_UNIT_OPTIONS}
          />
        )}
      </Box>

      <CheckboxGroupNew
        label="Personalizza dosaggi"
        value={isCustomDosagesEnabled ? ['custom'] : []}
        onChange={onCustomDosagesToggle}
        options={[{value: 'custom', label: 'Attiva dosaggi personalizzati'}]}
        sx={{mb: 2}}
      />

      {isCustomDosagesEnabled && (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
          {(value.customDosages ?? []).map((customDosage, index) => (
            <Card key={index}>
              <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5}}>
                <Typography sx={{fontWeight: 700, fontSize: 14, color: NEUTRAL_SCALE[900]}}>
                  {`Dosaggio ${index + 1}`}
                </Typography>
                {index !== 0 && (
                  <Button
                    variant="ghost"
                    startIcon={<DeleteOutlineIcon fontSize="small" />}
                    onClick={() => removeCustomDosage(index)}
                    sx={{color: 'error.main'}}
                  >
                    Rimuovi
                  </Button>
                )}
              </Box>
              <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: 2}}>
                <UnitValueField
                  label="Valore"
                  value={customDosage.dosage}
                  onChange={(dosage) => updateCustomDosage(index, {dosage})}
                  unitOptions={DOSAGE_UNIT_OPTIONS}
                />
                <UnitValueField
                  label="Velocità infusionale"
                  value={customDosage.infusionRate}
                  onChange={(infusionRate) => updateCustomDosage(index, {infusionRate})}
                  unitOptions={INFUSION_RATE_UNIT_OPTIONS}
                />
                <UnitValueField
                  label="Dosaggio secondario"
                  value={customDosage.secondaryDosage}
                  onChange={(secondaryDosage) => updateCustomDosage(index, {secondaryDosage})}
                  unitOptions={DOSAGE_UNIT_OPTIONS}
                />
                <TextFieldNew
                  label="Diluizione (ml)"
                  fullWidth
                  value={customDosage.dilution !== undefined && customDosage.dilution !== null ? String(customDosage.dilution) : ''}
                  onChange={(v) => updateCustomDosage(index, {dilution: v === '' ? undefined : Number(v)})}
                />
                <TimePickerNew
                  label="Orario di somministrazione"
                  value={customDosage.administrationHour ?? null}
                  onChange={(administrationHour) => updateCustomDosage(index, {administrationHour})}
                />
              </Box>
            </Card>
          ))}
          <Button variant="ghost" startIcon={<AddIcon fontSize="small" />} onClick={addCustomDosage} sx={{alignSelf: 'flex-start'}}>
            Aggiungi dosaggio
          </Button>
        </Box>
      )}
    </Box>
  )
}
